import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import UploadIcon from '@mui/icons-material/FileUploadOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useDateFormat } from '@/hooks/useDateFormat';
import { PERMISSIONS } from '@/types/auth';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import type { WarehouseDto } from '@/types/organization';
import type { ClientDto, MaterialDto, PagedResult } from '@/types/materials';
import type { CreateMaterialRequestRequest, MaterialRequestDto, MaterialRequestLineInput, UpdateMaterialRequestRequest } from '@/types/outbound';

type PlanMode = 'Manual' | 'Excel';

const EMPTY_LINE: MaterialRequestLineInput = { materialId: '', requestedQty: 0, remarks: null };

export default function MaterialRequestsPage() {
  const { t } = useTranslation('materialRequest');
  const { formatDate } = useDateFormat();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: config } = useProcessConfiguration();
  // Which mode(s) the screen offers is admin-configurable (Process Configuration > Material
  // Request) — choosing Manual, Excel Import, or both. Falls back to Manual if a company somehow
  // disables both, rather than rendering a dialog with no usable mode at all.
  const availableModes = (['Manual', 'Excel'] as PlanMode[]).filter((m) =>
    m === 'Manual' ? (config?.materialRequestManualEnabled ?? true) : (config?.materialRequestExcelImportEnabled ?? true),
  );
  if (availableModes.length === 0) availableModes.push('Manual');

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ['material-requests'],
    queryFn: async () => (await apiClient.get<MaterialRequestDto[]>('/material-requests')).data,
  });
  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses'],
    queryFn: async () => (await apiClient.get<WarehouseDto[]>('/warehouses')).data,
  });
  const { data: materialsPage } = useQuery({
    queryKey: ['materials', 'all-for-picker'],
    queryFn: async () => (await apiClient.get<PagedResult<MaterialDto>>('/materials', { params: { page: 1, pageSize: 200 } })).data,
  });
  // A Material Request is for requesting components to consume (raw materials/semi-finished
  // goods), never the finished output itself — Finished Goods are what a Production Receipt or
  // Dispatch Order deals in, not something you'd raise a request to pull off the shelf here.
  const materials = (materialsPage?.items ?? []).filter((m) => m.materialType !== 'FG');
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => (await apiClient.get<ClientDto[]>('/clients')).data,
    enabled: config?.thirdPartyLogisticsEnabled ?? false,
  });

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [mode, setMode] = useState<PlanMode>('Manual');
  const [warehouseId, setWarehouseId] = useState('');
  const [clientId, setClientId] = useState('');
  const [lines, setLines] = useState<MaterialRequestLineInput[]>([EMPTY_LINE]);
  const [excelErrors, setExcelErrors] = useState<string[]>([]);

  const resetForm = () => {
    setEditingId(null);
    setMode(availableModes[0]);
    setWarehouseId('');
    setClientId('');
    setLines([EMPTY_LINE]);
    setExcelErrors([]);
  };

  // Only a still-Draft request can be edited — mirrors MaterialRequestService.UpdateAsync's guard.
  const openEdit = (r: MaterialRequestDto) => {
    setEditingId(r.id);
    setMode('Manual');
    setWarehouseId(r.warehouseId);
    setClientId(r.clientId ?? '');
    setLines(r.lines.map((l) => ({ materialId: l.materialId, requestedQty: l.requestedQty, remarks: l.remarks })));
    setExcelErrors([]);
    setOpen(true);
  };

  const handleDownloadTemplate = () => {
    const worksheet = XLSX.utils.aoa_to_sheet([['Material Code', 'Material Name', 'Qty']]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Material Request');
    XLSX.writeFile(workbook, 'material-request-template.xlsx');
  };

  // Parses an uploaded workbook into the same line-list state Manual mode edits directly — the
  // operator reviews/adjusts the parsed rows before submitting.
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet);

    const errors: string[] = [];
    const parsedLines: MaterialRequestLineInput[] = [];
    rows.forEach((row, i) => {
      const code = String(row['Material Code'] ?? '').trim();
      if (!code) return;
      const qty = Number(row['Qty'] ?? 0);
      const material = materials.find((m) => m.code.toLowerCase() === code.toLowerCase());
      if (!material) {
        errors.push(t('form.excelRowMaterialNotFound', { row: i + 2, code }));
        return;
      }
      if (!(qty > 0)) {
        errors.push(t('form.excelRowInvalidQty', { row: i + 2, code }));
        return;
      }
      parsedLines.push({ materialId: material.id, requestedQty: qty, remarks: null });
    });

    setExcelErrors(errors);
    setLines(parsedLines.length > 0 ? parsedLines : [EMPTY_LINE]);
  };

  const createMutation = useMutation({
    mutationFn: () => {
      const cleanLines = lines.filter((l) => l.materialId);
      const resolvedClientId = config?.thirdPartyLogisticsEnabled ? clientId || null : null;
      if (editingId) {
        const payload: UpdateMaterialRequestRequest = { warehouseId, lines: cleanLines, clientId: resolvedClientId };
        return apiClient.put(`/material-requests/${editingId}`, payload);
      }
      const payload: CreateMaterialRequestRequest = { warehouseId, lines: cleanLines, clientId: resolvedClientId };
      return apiClient.post('/material-requests', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['material-requests'] });
      setOpen(false);
    },
  });

  const generatePickListMutation = useMutation({
    mutationFn: (materialRequestId: string) => apiClient.post(`/pick-lists/generate/${materialRequestId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['material-requests'] });
      queryClient.invalidateQueries({ queryKey: ['pick-lists'] });
    },
  });

  const columns: DataTableColumn<MaterialRequestDto>[] = [
    { field: 'mrNumber', headerName: t('table.number') },
    { field: 'warehouseName', headerName: t('table.warehouse'), filterable: true },
    { field: 'requestedByUserName', headerName: t('table.requestedBy'), filterable: true },
    { field: 'requestDate', headerName: t('table.date'), render: (r) => formatDate(r.requestDate) },
    { field: 'lines', headerName: t('table.lines'), render: (r) => r.lines.length },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    ...(config?.thirdPartyLogisticsEnabled
      ? [{ field: 'clientName', headerName: t('table.client'), render: (r: MaterialRequestDto) => r.clientName ?? '—', filterable: true } as DataTableColumn<MaterialRequestDto>]
      : []),
    {
      field: 'action', headerName: '', render: (r) => (
        <Stack direction="row" spacing={1}>
          {r.status === 'Draft' && (
            <IfPermitted permission={PERMISSIONS.MATERIAL_REQUEST_MANAGE}>
              <Button size="small" onClick={() => openEdit(r)}>
                {t('edit')}
              </Button>
            </IfPermitted>
          )}
          <IfPermitted permission={PERMISSIONS.PICKING_EXECUTE}>
            {r.status === 'Draft' && (
              <Button size="small" variant="outlined" disabled={generatePickListMutation.isPending} onClick={() => generatePickListMutation.mutate(r.id)}>
                {t('generatePickList')}
              </Button>
            )}
          </IfPermitted>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.MATERIAL_REQUEST_MANAGE}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setOpen(true);
              }}
            >
              {t('add')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={requests} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="material-requests" />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? t('edit') : t('add')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Which modes appear here is admin-configurable (Process Configuration > Material
                Request) — hidden entirely when only one mode is enabled, since there's nothing to
                toggle. Not shown while editing — an existing request's lines are always edited
                manually regardless of which mode created it. */}
            {availableModes.length > 1 && !editingId && (
              <ToggleButtonGroup
                color="primary"
                exclusive
                fullWidth
                size="small"
                value={mode}
                onChange={(_, value: PlanMode | null) => {
                  if (!value) return;
                  setMode(value);
                  setExcelErrors([]);
                }}
              >
                {availableModes.map((m) => (
                  <ToggleButton key={m} value={m}>
                    {t(`form.mode${m}`)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}

            <SearchableSelect
              label={t('form.warehouse')} value={warehouseId} onChange={setWarehouseId} fullWidth
              options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
            />

            {config?.thirdPartyLogisticsEnabled && (
              <SearchableSelect
                label={t('form.client')} value={clientId} onChange={setClientId} fullWidth
                options={clients.map((c) => ({ value: c.id, label: c.name }))}
              />
            )}

            {mode === 'Excel' && !editingId && (
              <>
                <Divider />
                <Stack spacing={1}>
                  <Typography variant="caption" color="text.secondary">
                    {t('form.excelHint')}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" startIcon={<DownloadIcon fontSize="small" />} onClick={handleDownloadTemplate}>
                      {t('form.downloadTemplate')}
                    </Button>
                    <Button size="small" variant="outlined" component="label" startIcon={<UploadIcon fontSize="small" />}>
                      {t('form.uploadFile')}
                      <input ref={fileInputRef} type="file" accept=".xlsx,.xls" hidden onChange={handleFileUpload} />
                    </Button>
                  </Stack>
                  {excelErrors.length > 0 && (
                    <Alert severity="error">
                      <Stack spacing={0.25}>
                        {excelErrors.map((err, i) => (
                          <Typography key={i} variant="body2">
                            {err}
                          </Typography>
                        ))}
                      </Stack>
                    </Alert>
                  )}
                </Stack>
              </>
            )}

            <Divider />

            {lines.map((line, idx) => (
              <Stack key={idx} direction="row" spacing={1} alignItems="center">
                <SearchableSelect
                  label={t('form.material')} value={line.materialId} fullWidth size="small"
                  onChange={(materialId) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, materialId } : l)))}
                  options={materials.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
                />
                <TextField
                  type="number" label={t('form.quantity')} value={line.requestedQty} size="small" sx={{ minWidth: 120 }}
                  onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, requestedQty: Number(e.target.value) } : l)))}
                />
                <IconButton size="small" onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))} disabled={lines.length <= 1}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setLines((prev) => [...prev, { ...EMPTY_LINE }])}
              sx={{ alignSelf: 'flex-start' }}
            >
              {t('form.addLine')}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button
            variant="contained"
            disabled={!warehouseId || lines.some((l) => !l.materialId || l.requestedQty <= 0) || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
